<script lang="ts">
  /**
   * Tenants-vy (S-04-33, read-only MVP1).
   *
   * Listar alla tenants som superadmin har tillgång till. MVP1 är
   * single-tenant-deployment (Hisings Backa), men datamodellen är
   * multi-tenant från dag ett enligt CLAUDE.md "Multi-tenancy och
   * identitet" (låst 2026-05-05). Vyn är därmed funktionellt nästan
   * tom i pilot-skedet — den listar bara seedade Hisings Backa-tenant
   * och eventuella test-tenants som superadmin skapar.
   *
   * ============================================================
   * Datakällor
   * ============================================================
   *  - GET /api/admin/tenants                  (tenantsClient.listTenants)
   *  - GET /api/admin/tenants/{id}/tier-stats  (tenantsClient.getTenantTierStats, S-04-36)
   *
   * Single-tenant get-funktionen (`getTenant`) finns i klienten men
   * används inte i den här vyn — sparas för Edit-modalen i S-04-34
   * som behöver hämta färska värden innan den öppnas.
   *
   * Tier-stats hämtas parallellt för alla listade tenants efter att
   * listan laddats. Backend applicerar k=5-skydd; värden 1..4
   * returneras som -1 och renderas som "<5" med varningsfärg.
   *
   * ============================================================
   * Synlighet (DOM-exklusion, inte CSS-gömning)
   * ============================================================
   * App.svelte villkorar tab-knappen + render-blocket på
   * `currentUser.isSuperadmin`. Den här komponenten antar att den
   * bara renderas för superadmins — om den ändå laddas som plain
   * admin returnerar backend 403 och vi visar ett tomt fel-meddelande.
   * Server-side gating (`requireSuperadmin`) är auktoritativ källa.
   *
   * ============================================================
   * Hard-rules (CLAUDE.md)
   * ============================================================
   *  - Inga fritextfält. Tenant-namn och slug är kategoriska
   *    identifierare som backend validerar (slug-regex, name-längd).
   *    Read-only här, så ingen input-validering behövs i den här vyn.
   *  - Inga råa databas-ID:n — UUID/opaque IDs överallt.
   *  - Cookie-auth via `credentials: 'include'`.
   *  - Tier-statistik renderas i kompakt format "T0: 142 / T1: <5 / ..."
   *    (S-04-36). Backend applicerar k=5-skydd.
   *  - Edit-knapp finns men är disabled — kommer i S-04-34.
   *  - Engelska kod-identifierare, svenska användartexter.
   */
  import { onMount } from 'svelte';
  import {
    formatTierCount,
    getTenantTierStats,
    guestPolicyLabel,
    isTierCountSuppressed,
    listTenants,
    tenantStatusLabel,
    type Tenant,
    type TierCounts,
  } from './tenantsClient';
  import TenantEditModal from './TenantEditModal.svelte';
  import { formatDate } from '../util/format';

  type Props = {
    /** Anropas vid 401/403 för att tvinga logout-flow i App.svelte. */
    onUnauthorized?: () => void;
  };
  const { onUnauthorized }: Props = $props();

  // ===================================================================
  // List-state
  // ===================================================================

  let tenants: Tenant[] = $state([]);
  let isLoading = $state(true);
  let listError: string | null = $state(null);

  /**
   * Tier-counts per tenant-id (S-04-36). Map så vi kan kolla per rad
   * utan att binda till tenants-arrayens index. Saknade entries =
   * "laddar fortfarande" → vi visar en placeholder.
   */
  let tierStats: Record<string, TierCounts> = $state({});

  /**
   * Aktiv tenant under redigering (S-04-34). `null` = modal stängd.
   * Modalen får sin pre-fyllda kopia via `tenant`-prop; vid lyckad save
   * returnerar den uppdaterad tenant som vi mergar in i listan utan
   * extra GET-rundtur.
   */
  let editingTenant: Tenant | null = $state(null);

  async function loadTenants(): Promise<void> {
    isLoading = true;
    listError = null;
    tierStats = {};
    const result = await listTenants();
    if (result.kind === 'ok') {
      tenants = result.items;
      // Hämta tier-stats parallellt för alla tenants. Fel per tenant
      // sväljs här — vi visar "—" för raden istället för att fela hela
      // vyn (en tenant kan ha gått 404 i race med list-anropet).
      void loadTierStats(result.items);
    } else if (result.kind === 'unauthorized') {
      tenants = [];
      listError = 'Sessionen har gått ut. Logga in igen.';
      onUnauthorized?.();
    } else {
      tenants = [];
      listError = result.message;
    }
    isLoading = false;
  }

  async function loadTierStats(items: Tenant[]): Promise<void> {
    // Promise.all parallelliserar — ingen meningsfull belastning på
    // backend i pilot-skedet (en tenant). Resultaten skrivs till en
    // ny map så Svelte:s reaktivitet upptäcker ändringen.
    const next: Record<string, TierCounts> = { ...tierStats };
    const results = await Promise.all(items.map((t) => getTenantTierStats(t.id)));
    for (let i = 0; i < items.length; i++) {
      const r = results[i];
      if (r.kind === 'ok') {
        next[items[i].id] = r.counts;
      } else if (r.kind === 'unauthorized') {
        // En 401/403 på stats-endpointet betyder samma som på list:
        // sessionen är ute. Tvinga logout-flow för konsistens.
        onUnauthorized?.();
        return;
      }
      // Övriga fel: lämna utan entry → UI visar placeholder för raden.
    }
    tierStats = next;
  }

  onMount(() => {
    void loadTenants();
  });

  function handleRefresh(): void {
    void loadTenants();
  }

  function openEditModal(tenant: Tenant): void {
    // Ta en defensiv kopia så att modal-formfältens reaktivitet inte
    // muterar tabell-radens objekt innan save.
    editingTenant = { ...tenant };
  }

  function closeEditModal(): void {
    editingTenant = null;
  }

  function handleTenantSaved(updated: Tenant): void {
    // Mergar in den nyligen returnerade raden i listan utan att förlora
    // ordning. Backend returnerar den färska raden så vi slipper en
    // extra GET-rundtur.
    tenants = tenants.map((t) => (t.id === updated.id ? updated : t));
    editingTenant = null;
  }
</script>

<section class="tenants" aria-labelledby="tenants-rubrik">
  <div class="header-row">
    <div>
      <h2 id="tenants-rubrik">Tenants</h2>
      <p class="lead">
        Compuna-superadmin: föreningar/tenants som kör Härden. MVP1 är single-tenant
        (Hisings Backa); fler tenants kan seedas inför multi-tenant-utrullning.
      </p>
    </div>
    <div class="header-actions">
      <button
        type="button"
        class="secondary"
        onclick={handleRefresh}
        disabled={isLoading}
      >
        {isLoading ? 'Laddar…' : 'Uppdatera'}
      </button>
    </div>
  </div>

  {#if listError}
    <div class="alert error" role="alert">{listError}</div>
  {/if}

  {#if isLoading && tenants.length === 0}
    <p class="empty">Laddar tenants…</p>
  {:else if tenants.length === 0 && !listError}
    <p class="empty">Inga tenants registrerade ännu.</p>
  {:else if tenants.length > 0}
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th scope="col">Namn</th>
            <th scope="col">Slug</th>
            <th scope="col">Gäst-policy</th>
            <th scope="col">Status</th>
            <th scope="col">Skapad</th>
            <th scope="col">Tier-statistik</th>
            <th scope="col" class="actions-col">Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {#each tenants as tenant (tenant.id)}
            <tr>
              <td>{tenant.name}</td>
              <td><code>{tenant.slug}</code></td>
              <td>{guestPolicyLabel(tenant.guestPolicy)}</td>
              <td>
                <span class="status" class:status-inactive={!tenant.isActive}>
                  {tenantStatusLabel(tenant.isActive)}
                </span>
              </td>
              <td>{formatDate(tenant.createdAt)}</td>
              <td class="tier-cell">
                {#if tierStats[tenant.id]}
                  {@const c = tierStats[tenant.id]}
                  <span class="tier-list">
                    <span class="tier-item" class:tier-suppressed={isTierCountSuppressed(c.tier0)}>
                      T0: {formatTierCount(c.tier0)}
                    </span>
                    <span class="tier-sep" aria-hidden="true">/</span>
                    <span class="tier-item" class:tier-suppressed={isTierCountSuppressed(c.tier1)}>
                      T1: {formatTierCount(c.tier1)}
                    </span>
                    <span class="tier-sep" aria-hidden="true">/</span>
                    <span class="tier-item" class:tier-suppressed={isTierCountSuppressed(c.tier2)}>
                      T2: {formatTierCount(c.tier2)}
                    </span>
                    <span class="tier-sep" aria-hidden="true">/</span>
                    <span class="tier-item" class:tier-suppressed={isTierCountSuppressed(c.tier3)}>
                      T3: {formatTierCount(c.tier3)}
                    </span>
                  </span>
                {:else}
                  <span class="muted" aria-label="Tier-statistik laddar">—</span>
                {/if}
              </td>
              <td class="actions-col">
                <button
                  type="button"
                  class="secondary small"
                  onclick={() => openEditModal(tenant)}
                  aria-label={`Redigera tenant ${tenant.name}`}
                >
                  Redigera
                </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>

{#if editingTenant}
  <TenantEditModal
    tenant={editingTenant}
    onSaved={handleTenantSaved}
    onCancel={closeEditModal}
    onUnauthorized={() => onUnauthorized?.()}
  />
{/if}

<style>
  .tenants {
    display: grid;
    gap: 1rem;
  }
  .header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    flex-wrap: wrap;
  }
  h2 {
    margin: 0 0 0.35rem;
    font-size: 1.4rem;
  }
  .lead {
    margin: 0;
    color: var(--text-dim);
    font-size: 0.9rem;
    max-width: 60ch;
  }
  .header-actions {
    display: flex;
    gap: 0.5rem;
  }
  .secondary {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--text);
    padding: 0.5rem 0.95rem;
    border-radius: 6px;
    font-size: 0.9rem;
    cursor: pointer;
    font-family: inherit;
  }
  .secondary:hover:not(:disabled) {
    border-color: var(--text-dim);
  }
  .secondary:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .secondary.small {
    padding: 0.35rem 0.7rem;
    font-size: 0.85rem;
  }
  .alert.error {
    background: var(--bg-elev);
    border: 1px solid #c0533f;
    border-left: 3px solid #c0533f;
    border-radius: 4px;
    padding: 0.75rem 1rem;
    color: var(--text);
    font-size: 0.9rem;
  }
  .empty {
    margin: 1rem 0;
    color: var(--text-dim);
    font-style: italic;
  }
  .table-wrapper {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.92rem;
  }
  th,
  td {
    padding: 0.6rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid rgba(232, 232, 234, 0.08);
    vertical-align: middle;
  }
  th {
    color: var(--text-dim);
    font-weight: 600;
    font-size: 0.85rem;
    letter-spacing: 0.02em;
  }
  td code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .actions-col {
    width: 1%;
    white-space: nowrap;
  }
  .status {
    display: inline-block;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    font-size: 0.78rem;
    background: var(--accent-soft);
    color: var(--glow);
    border: 1px solid var(--accent);
  }
  .status-inactive {
    background: transparent;
    color: var(--text-dim);
    border-color: var(--rule);
  }
  .muted {
    color: var(--text-dim);
  }
  /* S-04-36 — tier-statistik per tenant. Kompakt format
     "T0: 142 / T1: <5 / T2: 0 / T3: 3" så raden inte tar för mycket
     bredd. Suppressed-värdet (k=5-skydd, "<5") får varningsfärg så
     superadmin tydligt ser att backend maskerade siffran. */
  .tier-cell {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .tier-list {
    display: inline-flex;
    align-items: baseline;
    gap: 0.25rem;
    font-size: 0.85rem;
  }
  .tier-item {
    color: var(--text);
  }
  .tier-suppressed {
    /* k=5-skyddat värde — tydlig varningsfärg så superadmin ser att
       siffran är maskerad (inte "saknas"). */
    color: #d99a3f;
    font-weight: 600;
  }
  .tier-sep {
    color: var(--text-dim);
  }
</style>
